import { useEffect, useState } from "react";
import { BookOpen } from "lucide-react";
import { supabase } from "../lib/supabase";
import type { AdsDecision } from "../lib/types";
import { CHANNEL_LABEL, ACTION_LABEL, fmtDate } from "../lib/format";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending:  { label: "Pendente",  cls: "bg-[#fef3c7] text-[#92400e]" },
  approved: { label: "Aprovada",  cls: "bg-[#dcfce7] text-[#166534]" },
  rejected: { label: "Rejeitada", cls: "bg-[#fee2e2] text-[#991b1b]" },
  executed: { label: "Executada", cls: "bg-[#dbeafe] text-[#1e40af]" },
  expired:  { label: "Expirada",  cls: "bg-esm-gray-100 text-esm-gray-700" },
};

export default function History() {
  const [decisions, setDecisions] = useState<AdsDecision[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      let query = supabase.from("ads_decisions").select("*").order("created_at", { ascending: false }).limit(200);
      if (statusFilter !== "all") query = query.eq("status", statusFilter);
      if (channelFilter !== "all") query = query.eq("channel", channelFilter);
      const { data } = await query;
      setDecisions((data as AdsDecision[]) ?? []);
      setLoading(false);
    };
    load();
  }, [statusFilter, channelFilter]);

  const selectCls = "border border-esm-gray-200 rounded-xl px-3 py-1.5 bg-white text-xs font-semibold text-esm-gray-700 focus:outline-none focus:ring-2 focus:ring-esm-brown-500/30";

  return (
    <>
      <PageHeader
        title="Histórico"
        subtitle="Decisões geradas pelo agente nos últimos períodos"
        icon={<BookOpen size={22} strokeWidth={2} />}
        actions={
          <div className="flex gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
              <option value="all">Todos status</option>
              <option value="pending">Pendentes</option>
              <option value="approved">Aprovadas</option>
              <option value="rejected">Rejeitadas</option>
              <option value="executed">Executadas</option>
              <option value="expired">Expiradas</option>
            </select>
            <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} className={selectCls}>
              <option value="all">Todos canais</option>
              <option value="meta">Meta</option>
              <option value="google">Google</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>
        }
      />

      <Card padded={false}>
        {loading ? <div className="p-10 text-esm-gray-400 italic text-center">Carregando…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-esm-cream-50">
                <tr className="text-left text-esm-gray-500 text-[11px] uppercase tracking-wider">
                  <th className="p-3">Data</th>
                  <th className="p-3">Canal</th>
                  <th className="p-3">Tier</th>
                  <th className="p-3">Ação</th>
                  <th className="p-3">Alvo</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {decisions.map((d) => {
                  const badge = STATUS_BADGE[d.status] ?? STATUS_BADGE.pending;
                  return (
                    <tr key={d.id} className="border-t border-esm-gray-100 hover:bg-esm-cream-50/40">
                      <td className="p-3 text-esm-gray-700 text-xs">{fmtDate(d.created_at)}</td>
                      <td className="p-3 text-esm-gray-900 font-semibold">{CHANNEL_LABEL[d.channel]}</td>
                      <td className="p-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${d.tier === "T1" ? "bg-[#dcfce7] text-[#166534]" : d.tier === "T2" ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#fee2e2] text-[#991b1b]"}`}>{d.tier}</span>
                      </td>
                      <td className="p-3 text-esm-gray-900">{ACTION_LABEL[d.action] ?? d.action}</td>
                      <td className="p-3 text-esm-gray-700 max-w-[300px] truncate" title={d.target_name}>{d.target_name}</td>
                      <td className="p-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${badge!.cls}`}>{badge!.label}</span>
                      </td>
                    </tr>
                  );
                })}
                {decisions.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-esm-gray-400 italic">Sem decisões nesse filtro.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
